import { CountryFlag, Typography } from '@dreamteam-monorepo/ui';
import { formatUkTime, formatUkDateShort } from '@/lib/ukTime';
import { cn } from '@/lib/utils';
import { PlayerMatch } from '@/lib/network/players';

/**
 * The fixture header of a match card. The match number and competition live in
 * the accordion bar underneath it (see `MatchAccordion`), so this stays a
 * summary of the fixture itself: who played, the score or kick-off, when and
 * where.
 */
export const FixtureItem = ({ fixtureProps }: { fixtureProps: PlayerMatch }) => {
  const { awayTeam, homeTeam, kickoffAt, status, venue } = fixtureProps;

  const time = formatUkTime(kickoffAt); // e.g. "3:41pm"
  const date = formatUkDateShort(kickoffAt); // e.g. "Tue 8 Jul"

  return (
    <div className="bg-[--page-header-bg-grey] p-[12px]">
      <div className="flex items-center gap-5">
        <div className="flex min-w-0 flex-1 items-center justify-end gap-[8px]">
          <Typography variant="paragraph010" className="truncate">
            {homeTeam.name}
          </Typography>
          <CountryFlag
            className="size-[20px] shrink-0 rounded-full object-cover"
            country={homeTeam.flagKey || ''}
            fallbackCountry=""
          />
        </div>
        <div className="flex shrink-0 flex-col items-center">
          {status === 'played' || status === 'playing' ? (
            <span className="rounded-t-[4px] bg-white px-[8px] py-[2px] w-full text-center">
              <Typography variant="subheadline020">
                {homeTeam.score ?? '0'} - {awayTeam.score ?? '0'}
              </Typography>
            </span>
          ) : status === 'fixture' ? (
            <span className="rounded-t-[4px] bg-white px-[8px] py-[2px]">
              <Typography variant="subheadline020">{time}</Typography>
            </span>
          ) : null}
          {status !== 'fixture' && (
            <span
              className={cn(
                'rounded-b-[4px] px-[8px] py-[2px] bg-[#201D4A] text-white w-full flex items-center justify-center',
                {
                  'bg-[--stats-form-win-bg] text-[#222526]': status === 'playing',
                  'bg-[#201D4A]': status === 'played',
                }
              )}
            >
              <Typography variant="paragraph000" className="text-[10px]">
                {status === 'playing' ? 'Live' : status === 'played' ? 'FT' : null}
              </Typography>
            </span>
          )}
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-start gap-[8px]">
          <CountryFlag
            className="size-[20px] shrink-0 rounded-full object-cover"
            country={awayTeam.flagKey || ''}
            fallbackCountry=""
          />
          <Typography variant="paragraph010" className="truncate">
            {awayTeam.name}
          </Typography>
        </div>
      </div>

      {(date || venue) && (
        <div className="mt-2 flex items-center justify-center divide-x">
          {date && (
            <div className="px-2">
              <Typography variant="paragraph000">{date}</Typography>
            </div>
          )}
          {venue && (
            <div className="px-2">
              <Typography variant="paragraph000">{venue}</Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
